import { PrimarySideListV2 } from '@dealstack/frontend/shared/components/list/PrimarySideList/PrimarySideListV2';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	Content,
	InvestmentContentWrapper,
	InvestmentHeader,
	Layout,
	MainLayout,
} from '@dealstack/frontend/investor/pages/Investment/common/investmentLayout';
import { INVESTMENT_PORTAL } from '@dealstack/frontend/investor/pages/Investment/common/renderHeaderPortal';
import {
	INVESTMENT_JOIN_ROUTES,
	useInvestmentJoinAndAllocationController,
} from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/useInvestmentJoinAndAllocationController';
import { Title } from '@mantine/core';
import React from 'react';
import { Route } from 'react-router-dom';
import { MainHeaderBar } from '@dealstack/frontend/investor/pages/Investment/common/MainHeaderBar';
import { JoinAndAllocationPersonalDetails } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationPersonalDetails';
import { JoinAndAllocationChecksComplete } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationChecksComplete/JoinAndAllocationChecksComplete';
import { JoinAndAllocationContracts } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationContracts/JoinAndAllocationContracts';
import { JoinAndAllocationCompletedStep } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationCompletedStep/JoinAndAllocationCompletedStep';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry';
import {
	NoSaveStep,
	WizardStepRegistryProvider,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';

export const investmentJoinAndAllocationContext = React.createContext<
	ReturnType<typeof useInvestmentJoinAndAllocationController>
>({} as ReturnType<typeof useInvestmentJoinAndAllocationController>);

interface Props {
	className?: string;
}

export const InvestmentJoinAndAllocation = ({ className }: Props) => {
	const investmentJoinController = useInvestmentJoinAndAllocationController();
	const {
		isLoading,
		isInitialDataReady,
		activeMainStep,
		onNextStep,
		isContinueDisabled,
		sideBarConfig,
		activeStepKey,
		onPreviousStep,
		isBackDisabled,
		activeMainStepArrayIndex,
		activeSubStepArrayIndex,
		sideBarItems,
		activeSubStep,
		isLastStep,
		onSidebarNavigation,
		redirectLink,
	} = investmentJoinController;

	return (
		<Layout className={className}>
			{isLoading || !isInitialDataReady ? <InlineLoader /> : null}
			<investmentJoinAndAllocationContext.Provider value={investmentJoinController}>
				<WizardStepRegistryProvider registry={investmentJoinController.stepRegistry}>
					<MainHeaderBar
						title={activeMainStep?.title}
						activeMainStepIndex={activeMainStepArrayIndex}
						activeSubStepIndex={activeSubStepArrayIndex}
						sidebarItemsCount={sideBarConfig?.length}
						onPrevStep={onPreviousStep}
						isPrevDisabled={isBackDisabled}
						onNextStep={onNextStep}
						isNextDisabled={isContinueDisabled}
						isLastStep={isLastStep}
						redirectLink={redirectLink}
					/>

					<MainLayout>
						<PrimarySideListV2
							sideListItems={sideBarItems ?? []}
							activeKey={activeStepKey}
							onActiveChange={onSidebarNavigation}
						/>

						<Content>
							{activeSubStep?.id !== 'KYC_INSTRUCTIONS' && (
								<InvestmentHeader>
									<Title order={5}>
										{activeSubStep?.title ?? activeMainStep?.title}
									</Title>
									<div id={INVESTMENT_PORTAL.SECTION_HEADER} />
								</InvestmentHeader>
							)}
							<InvestmentContentWrapper>
								<SentryRoutes>
									<Route
										path={`${INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS}/*`}
										element={<JoinAndAllocationPersonalDetails />}
									/>
									<Route
										path={`${INVESTMENT_JOIN_ROUTES.CHECKS_COMPLETE}/*`}
										element={<JoinAndAllocationChecksComplete />}
									/>
									<Route
										path={INVESTMENT_JOIN_ROUTES.CONTRACTS}
										element={
											<NoSaveStep>
												<JoinAndAllocationContracts />
											</NoSaveStep>
										}
									/>
									<Route
										path={INVESTMENT_JOIN_ROUTES.COMPLETED}
										element={
											<NoSaveStep>
												<JoinAndAllocationCompletedStep />
											</NoSaveStep>
										}
									/>
								</SentryRoutes>
							</InvestmentContentWrapper>
						</Content>
					</MainLayout>
				</WizardStepRegistryProvider>
			</investmentJoinAndAllocationContext.Provider>
		</Layout>
	);
};
